"use client";

import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { DestinationInterLinkingSection as DestinationInterLinkingSectionType, DestinationInterLinkingItem } from "@/types/dto/destinations-page/destination-page-types";
import { injectSourceIntoUrl } from "@/config/unified-search-config";
import {useTranslations} from "next-intl";
import DestinationChips from "./destination-chips";

export interface DestinationInterLinkingSectionProps {
  destinationInterLinkingSection: DestinationInterLinkingSectionType;
  destinationType?: "destinationContinent" | "destinationCountry" | "destinationCity";
  currentDestination?: string;
  parentContinent?: string;
  parentCountry?: string;
}

export const DestinationInterLinkingSection: React.FC<DestinationInterLinkingSectionProps> = ({
  destinationInterLinkingSection,
  destinationType,
  currentDestination,
  parentContinent, // eslint-disable-line @typescript-eslint/no-unused-vars
  parentCountry
}) => {
  const { destinationCarousel, otherDestinations } = destinationInterLinkingSection;

  const t = useTranslations("DESTINATIONS");

  // Transform API data to component format
  const transformDestination = (item: DestinationInterLinkingItem) => ({
    id: item.destinationName,
    name: item.destinationName,
    slug: item.destinationName.toLowerCase().replace(/\s+/g, "-"),
    destinationType: item.destinationType,
    destinationUrl: injectSourceIntoUrl(item.destinationUrl, "destination_interlinking")
  });

  // Apply filtering and sorting based on page type
  const filterAndSortDestinations = (destinations: DestinationInterLinkingItem[]) => {
    let filtered = [...destinations];

    // Apply filtering rules based on page type
    if (destinationType === "destinationContinent") {
      // For continent pages: list all countries under that continent alphabetically
      filtered = filtered.filter(d => d.destinationType === "destinationCountry");
      filtered.sort((a, b) => a.destinationName.localeCompare(b.destinationName));
    } else if (destinationType === "destinationCountry") {
      // For country pages: list cities under that country (max 32)
      filtered = filtered.filter(d => d.destinationType === "destinationCity");
      filtered = filtered.slice(0, 32); // Max 32 cities
    } else if (destinationType === "destinationCity") {
      // For city pages: list other cities within that city's country, excluding current city
      filtered = filtered.filter(d =>
        d.destinationType === "destinationCity" &&
        d.destinationName.toLowerCase() !== currentDestination?.toLowerCase());
      filtered = filtered.slice(0, 32); // Max 32 cities
    }

    return filtered;
  };

  const filteredDestinations = filterAndSortDestinations(destinationCarousel.destinations);
  const relatedDestinations = filteredDestinations.map(transformDestination);

  // Filter the other regions section
  let filteredOtherDestinations = [...otherDestinations];

  // For continent pages, exclude current continent
  if (destinationType === "destinationContinent" && currentDestination) {
    filteredOtherDestinations = filteredOtherDestinations.filter(
      d => d.destinationName.toLowerCase() !== currentDestination.toLowerCase()
    );
  }

  const otherContinents = filteredOtherDestinations.map(item => ({
    ...transformDestination(item),
    destinationType: item.destinationType || "continent" // Default to continent for "Other regions"
  }));

  const resolveDestination = () => {
    if (destinationType === "destinationCity") {
      // For city pages: "Other {country} destinations"
      return parentCountry?.split("-").join(" ") || "";
    }
    // For continent/country pages: "Other {current page} destinations"
    return currentDestination?.split("-").join(" ") || "";
  };

  return (
    <Stack
      spacing={8}
      data-track="visibility"
      data-parent-container-type="links"
      data-module-name="Destination Chip Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position="19"
      component="section"
      sx={{
        py: {
          xs: 6, // 48px on extra small screens
          sm: 6 // 48px on small screens and up
        },
        pb: {
          xs: 8, // 64px bottom padding on extra small screens
          sm: 6 // 48px bottom padding on small screens and up
        }
      }}
    >
      {/* Section 1: Destination-specific interlinking */}
      {relatedDestinations.length > 1 && (
        <Box>
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              mb: 4,
              color: "text.primary",
              fontSize: {
                legacyMobile: "26px",
                legacyTablet: "32px",
                legacyDesktop: "32px"
              },
              fontWeight: 600,
              lineHeight: {
                legacyMobile: "normal",
                legacyTablet: "130%",
                legacyDesktop: "130%"
              }
            }}
            data-testid="related-destinations-header"
          >
            {t.rich("Other destinations", {
              destination: resolveDestination(),
              // eslint-disable-next-line react/no-unstable-nested-components
              span: (chunks) => (
                <span style={{
                  textTransform: "capitalize"
                }}>
                {chunks}
              </span>
              )})
            }
          </Typography>
          <DestinationChips destinations={relatedDestinations} />
        </Box>
      )}

      {/* Section 2: Other regions we visit */}
      {otherContinents.length > 0 && (
        <Box>
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              mb: 4,
              color: "text.primary",
              fontSize: {
                legacyMobile: "26px",
                legacyTablet: "32px",
                legacyDesktop: "32px"
              },
              fontWeight: 600,
              lineHeight: {
                legacyMobile: "normal",
                legacyTablet: "130%",
                legacyDesktop: "130%"
              }
            }}
            data-testid="other-regions-header"
          >
            Other regions we visit
          </Typography>
          <DestinationChips destinations={otherContinents} />
        </Box>
      )}
    </Stack>
  );
};